import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataContext } from '../DataContext';
import { Stack, Spinner, Button, FormControl, Image } from 'react-bootstrap';
import DOMPurify from 'dompurify';

// Configure DOMPurify safety rules
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'img', 'div'],
  ALLOWED_ATTR: ['href', 'title', 'target', 'src', 'alt', 'class', 'width', 'height'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
};

const Chatbot = () => {
  const { t } = useTranslation();
  const { globalData } = useDataContext();
  const appLanguage = localStorage.getItem('appLanguage') || 'en';
  const SiteName = globalData ? globalData.sg_name : t('SiteName');

  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNewBotMessage, setIsNewBotMessage] = useState(false);
  const onTypingEnd = useCallback(() => {
    setIsNewBotMessage(false);
  }, []);

  const typingSound = useRef(null);
  const sendSound = useRef(null);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    typingSound.current = new Audio('/typing.mp3');
    sendSound.current = new Audio('/send.mp3');

    const savedMessages = JSON.parse(localStorage.getItem('chatMessages')) || [];
    setMessages(savedMessages);

    const tabId = Date.now().toString();
    sessionStorage.setItem(`chatbot-tab-${tabId}`, 'active');

    const cleanUp = () => {
      sessionStorage.removeItem(`chatbot-tab-${tabId}`);
      setTimeout(() => {
        const stillOpen = Object.keys(sessionStorage).some(key => key.startsWith('chatbot-tab-'));
        if (!stillOpen) {
          localStorage.removeItem('chatMessages');
        }
      }, 500);
    };
    const handleBeforeUnload = () => cleanUp();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') cleanUp();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {  
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleUserInput = (event) => setUserInput(event.target.value);

  const handleSendMessage = async () => {
    if (userInput.trim() === "") return;
    sendSound.current?.play();

    const newMessages = [...messages, { text: userInput, sender: 'user' }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const botResponse = await fetchBotResponse(userInput);
      const updatedMessages = [...newMessages, { text: botResponse, sender: 'bot' }];
      setMessages(updatedMessages);
      localStorage.setItem('chatMessages', JSON.stringify(updatedMessages));
      setIsNewBotMessage(true);
    } catch (error) {
      console.error('Bot fetch error:', error);
      const errorMessage = t('ChatbotError');
      const updatedMessages = [...newMessages, { text: errorMessage, sender: 'bot' }];
      setMessages(updatedMessages);
      localStorage.setItem('chatMessages', JSON.stringify(updatedMessages));
    }

    setLoading(false);
    setUserInput('');
  };

  const fetchBotResponse = async (query) => {
    const response = await fetch(`${global.config.API_URL}chatbot/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: query }),
    });

    if (!response.ok) throw new Error('Failed to fetch response');
    const data = await response.json();
    return data.response || 'No response from bot';
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleClearChat = () => {
    if (window.confirm(t('ClearChatAlert'))) {
      setMessages([]);
      localStorage.removeItem('chatMessages');
    }
  };

  return (
    <div id='igdtuw-chatbot'>
      <div className={isOpen ? "chatbot-icon d-none" : "chatbot-icon"} onClick={() => setIsOpen(true)}>
        <div className="chatbot-hover-content">
          <div className="chatbot-spinners position-absolute top-50 start-50 translate-middle">
            <Spinner animation="grow" variant="success" size="sm" />
            <Spinner animation="grow" variant="success" size="sm" />
            <Spinner animation="grow" variant="success" size="sm" />
          </div>
          <svg viewBox="0 0 32 32" width={32} height={32} className="chatbot-svg">
            <path className="icon-part-1" d="M12.63,26.46H8.83a6.61,6.61,0,0,1-6.65-6.07,89.05,89.05,0,0,1,0-11.2A6.5,6.5,0,0,1,8.23,3.25a121.62,121.62,0,0,1,15.51,0A6.51,6.51,0,0,1,29.8,9.19a77.53,77.53,0,0,1,0,11.2,6.61,6.61,0,0,1-6.66,6.07H19.48L12.63,31V26.46" />
            <path className="icon-part-2" d="M19.57,21.68h3.67a2.08,2.08,0,0,0,2.11-1.81,89.86,89.86,0,0,0,0-10.38,1.9,1.9,0,0,0-1.84-1.74,113.15,113.15,0,0,0-15,0A1.9,1.9,0,0,0,6.71,9.49a74.92,74.92,0,0,0-.06,10.38,2,2,0,0,0,2.1,1.81h3.81V26.5Z" />
            <path className="icon-part-3" d="M10.1323 14.052C9.61464 13.873 9 14.1779 9 14.6347V16.9776C9 17.3027 9.21685 17.6019 9.57923 17.7403C10.6835 18.1618 13.228 19 16.0001 19C18.7721 19 21.3166 18.1618 22.4209 17.7403C22.7833 17.6019 23 17.3027 23 16.9776V14.6347C23 14.1779 22.3855 13.873 21.8678 14.052C20.5905 14.4935 18.3791 15.1109 16.0001 15.1109C13.621 15.1109 11.4096 14.4935 10.1323 14.052Z" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="chatbox">
          <div className='chatbox-header d-flex justify-content-between align-items-center'>
            <div className='chatbox-title d-flex align-items-center me-2 flex-grow-1'>
              <Image src={global.config.uploadPath + '2041938450.png'} alt={appLanguage === 'hi' ? `${SiteName} प्रतीक चिन्ह` : `${SiteName} logo`} loading="lazy" fluid width={48} height={48} />
              <div className='ms-2'>
                <div className='fw-bold'>{t('IGDTUW')}</div>
                <small className='fw-normal'>{t('CHATBOT')}</small>
              </div>
              <div className='ms-2'>
                <i className="bi bi-robot fs-2"></i>
              </div>
            </div>
            <Stack direction="horizontal" gap={2}>
              <Button variant='link' className="fs-4 p-0 text-white" onClick={handleClearChat} title={t('ClearChat')}>
                <i className="bi bi-trash3"></i>
              </Button>
              <Button variant='link' className="fs-4 p-0 text-white" onClick={() => setIsOpen(false)} title={t('CloseChat')}>
                <i className="bi bi-x-circle-fill"></i>
              </Button>
            </Stack>
          </div>

          <div className="message-box" ref={scrollRef} style={{ overflowY: 'auto' }}>
            {messages.length === 0 ? (
              <div className="message-list">                
                <TypingHTMLMessage
                  html={t('ChatbotWelcome')}
                  className="message-bot"
                  typingSound={typingSound} 
                  onTypingEnd={onTypingEnd}
                />
              </div>
            ) : (
              messages.map((msg, index) => {
                const isLast = index === messages.length - 1;
                const isBot = msg.sender === 'bot';
                const shouldType = isLast && isBot && isNewBotMessage;

                return (
                  <div key={index} className="message-list">
                    {shouldType ? (
                        <TypingHTMLMessage
                          html={msg.text}
                          className={`message-${msg.sender}`}
                          typingSound={typingSound}
                          onTypingUpdate={scrollToBottom} 
                          onTypingEnd={onTypingEnd}
                        />
                    ) : (
                      <div
                        className={`message-${msg.sender}`}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.text, SANITIZE_CONFIG) }}
                      />
                    )}
                  </div>
                );
              })
            )}
            {loading && (
              <div className="message-list">
                <div className="message-bot">
                  <Stack direction="horizontal" gap={2}>
                    <Spinner animation="grow" variant="primary" size="sm" />
                    <Spinner animation="grow" variant="success" size="sm" />
                    <Spinner animation="grow" variant="danger" size="sm" />
                    <Spinner animation="grow" variant="warning" size="sm" />
                  </Stack>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="input-area">
            <FormControl size='lg' className='rounded-4' value={userInput} onChange={handleUserInput} placeholder={t('ChatWithUs')} autoComplete="off" autoFocus 
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && userInput.trim()) {
                e.preventDefault();
                handleSendMessage();
              }
            }} />
            <Button variant='link' size='sm' className='position-absolute top-50 end-0 translate-middle' onClick={handleSendMessage} disabled={!userInput.trim()}>
              <i className="bi bi-send-fill"></i>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;

const TypingHTMLMessage = ({ html, className = '', typingSound, onTypingUpdate, onTypingEnd }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';

    // Sanitize the HTML BEFORE typing/rendering it to the DOM
    const sanitizedHtml = DOMPurify.sanitize(html, SANITIZE_CONFIG);

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${sanitizedHtml}</div>`, 'text/html');
    const root = doc.body.firstChild;

    const cloneAndType = async (sourceNode, targetParent) => {
      if (sourceNode.nodeType === Node.TEXT_NODE) {
        const text = sourceNode.textContent;
        const span = document.createElement('span');
        targetParent.appendChild(span);

        for (let i = 0; i < text.length; i++) {
          span.textContent += text[i];
          typingSound?.current?.play();
          onTypingUpdate?.();
          await new Promise(resolve => setTimeout(resolve, 20));
        }
      } else if (sourceNode.nodeType === Node.ELEMENT_NODE) {
        const newEl = document.createElement(sourceNode.tagName);
        for (const attr of sourceNode.attributes) {
          // Strictly forbid event handlers or inline javascript
          if (!attr.name.startsWith('on') && !attr.value.startsWith('javascript:')) {
            newEl.setAttribute(attr.name, attr.value);
          }
        }
        targetParent.appendChild(newEl);

        for (const child of sourceNode.childNodes) {
          await cloneAndType(child, newEl);
        }
      }
    };

    const startTyping = async () => {
      if (root) {
        await cloneAndType(root, container);
      }
      onTypingEnd?.();
    };

    startTyping();
  }, [html, typingSound, onTypingUpdate, onTypingEnd]);

  return <div ref={containerRef} className={className}></div>;
};